import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { AdminFeePurchased } from '@models/invoice/admin-fee-purchased';
import { ClinicSupplyPurchased } from '@models/invoice/clinic-supply-purchased';
import { ClinicSupplyReturn } from '@models/invoice/clinic-supply-return';
import { Invoice } from '@models/invoice/invoice';
import { PaidCancellation } from '@models/invoice/paid-cancellation';
import { ProductPurchased } from '@models/invoice/product-purchased';
import { ServicePurchased } from '@models/invoice/service-purchased';
import { ClinicServiceTemplate } from '@models/service/clinic-service-template';
import { PlannedTreatment } from '@models/treatment-planning/planned-treatment';
import { PlannedTreatmentMultiple } from '@models/treatment-planning/planned-treatment-multiple';
import { TreatmentState } from '@models/treatment-planning/treatment-state';
import { FinanceService } from '@services/finance.service';
import { InvoicesService } from '@services/invoices.service';
import { ReturnService } from '@services/return.service';
import { TreatmentPlanService } from '@services/treatment-planning/treatment-plan.service';
import { Subject, forkJoin } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { PaymentType } from '../invoice-payment/invoice-payment.component';
import { PatientService } from './../../../../services/patient.service';

@Component({
  selector: 'app-return-payment',
  templateUrl: './return-payment.component.html',
  styleUrls: ['./return-payment.component.less'],
})
export class ReturnPaymentComponent implements OnInit, OnDestroy {
  @Output() invoicePay: EventEmitter<boolean> = new EventEmitter<boolean>();
  paidCancellation: PaidCancellation;
  TreatmentState = TreatmentState;
  patientId: number;
  productsPurchased: ProductPurchased[];
  creditsPurchased: number;
  servicesPurchased: ServicePurchased[];
  adminFeesPurchased: AdminFeePurchased[];
  paidCancellations: PaidCancellation[];
  clinicSupplies: ClinicSupplyPurchased[] = [];
  serviceTemplates: ClinicServiceTemplate[];
  returnForm: FormGroup;
  selectedInvoice: Invoice;
  isOpenPaymentModal = false;

  unsub = new Subject<any>();
  loading = false;
  plannedTreatmentMultiple: PlannedTreatmentMultiple;
  isPlannedTreatmentMultiple: boolean = false;
  plannedTreatmentMultipleList: PlannedTreatment[] = [];
  plannedTreatmentMultiplesForm: FormGroup;
  PaymentType = PaymentType;

  private readonly returnHandlers: Partial<Record<PaymentType, () => Promise<Invoice | undefined>>> = {
    [PaymentType.RefundProduct]: () => this.handleProductReturn(),
    [PaymentType.RefundClinicSupply]: () => this.handleClinicSupplyReturn(),
    [PaymentType.RefundService]: () => this.handleServiceReturn(),
    [PaymentType.RefundCredit]: () => this.handleCreditReturn(),
    [PaymentType.RefundAdminFee]: () => this.handleAdminFeeReturn(),
    [PaymentType.RefundPaidCancellation]: () => this.handlePaidCancellationReturn(),
  };

  private readonly nextDisabledChecks: Partial<Record<PaymentType, () => boolean>> = {
    [PaymentType.RefundProduct]: () => !this.formReturnProducts.valid,
    [PaymentType.RefundClinicSupply]: () => !this.formReturnClinicSupplies.valid,
    [PaymentType.RefundService]: () => !this.formReturnServices.valid,
    [PaymentType.RefundCredit]: () => !this.formReturnCredits.valid,
    [PaymentType.RefundAdminFee]: () => !this.formReturnFees.valid,
    [PaymentType.RefundPaidCancellation]: () => !this.formPaidCancellations.valid,
  };

  private getFormArray(name: string): FormArray {
    return (this.returnForm?.get(name) as FormArray) ?? this.formBuilder.array([]);
  }

  get formReturnProducts(): FormArray {
    return this.getFormArray('formReturnProducts');
  }

  get formReturnClinicSupplies(): FormArray {
    return this.getFormArray('formReturnClinicSupplies');
  }

  get formReturnServices(): FormArray {
    return this.getFormArray('formReturnServices');
  }

  get formReturnCredits(): FormArray {
    return this.getFormArray('formReturnCredits');
  }

  get formReturnFees(): FormArray {
    return this.getFormArray('formReturnFees');
  }

  get formPaidCancellations(): FormArray {
    return this.getFormArray('formPaidCancellations');
  }

  get returnTypeControl(): FormControl {
    return this.returnForm?.get('returnType') as FormControl;
  }

  get formReturnProductsControls(): FormGroup[] {
    return this.formReturnProducts.controls as FormGroup[];
  }

  get formReturnClinicSuppliesControls(): FormGroup[] {
    return this.formReturnClinicSupplies.controls as FormGroup[];
  }

  get formReturnServicesControls(): FormGroup[] {
    return this.formReturnServices.controls as FormGroup[];
  }

  get formReturnFeesControls(): FormGroup[] {
    return this.formReturnFees.controls as FormGroup[];
  }

  get formReturnCreditsControls(): FormGroup[] {
    return this.formReturnCredits.controls as FormGroup[];
  }

  get formPaidCancellationsControls(): FormGroup[] {
    return this.formPaidCancellations.controls as FormGroup[];
  }

  get returnType(): PaymentType {
    return this.returnForm?.get('returnType').value;
  }

  constructor(
    private formBuilder: FormBuilder,
    private invoicesService: InvoicesService,
    private financeService: FinanceService,
    private patientService: PatientService,
    private treatmentPlanService: TreatmentPlanService,
    private returnService: ReturnService,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.getPatientId();
    this.initForm();
    this.detectInvoicePaid();
    this.getPurchases();
  }

  private async getPurchases() {
    this.loading = true;
    await Promise.all([
      this.getProductsPurchased(),
      this.getCreditsPurchased(),
      this.getServicesPurchased(),
      this.getAdminFeesPurchased(),
      this.getPaidCancellations(),
      this.getClinicSupplies(),
    ]);
    this.loading = false;
  }

  private initForm() {
    this.returnForm = this.formBuilder.group({
      formReturnProducts: this.formBuilder.array([this.createProduct()]),
      formReturnClinicSupplies: this.formBuilder.array([this.createClinicSupply()]),
      formReturnCredits: this.formBuilder.array([this.createCredit()]),
      formReturnServices: this.formBuilder.array([this.createService()]),
      formReturnFees: this.formBuilder.array([this.createFee()]),
      formPaidCancellations: this.formBuilder.array([this.createPaidCancellation()]),
      returnType: new FormControl(PaymentType.RefundProduct, Validators.required),
    });

    this.plannedTreatmentMultipleList = [];
    this.plannedTreatmentMultiplesForm = this.formBuilder.group({});
    this.plannedTreatmentMultiple = null;
  }

  createProduct(): FormGroup {
    return this.formBuilder.group({
      product: new FormControl(null, Validators.required),
      productQuantity: new FormControl(null, [Validators.required, this.productQuantityValidator()]),
    });
  }

  createClinicSupply(): FormGroup {
    return this.formBuilder.group({
      clinicSupply: new FormControl(null, Validators.required),
      clinicSupplyQuantity: new FormControl(null, [Validators.required, this.clinicSupplyQuantityValidator()]),
    });
  }

  createCredit(): FormGroup {
    return this.formBuilder.group({
      creditQuantity: new FormControl(null, [Validators.required, this.creditQuantityValidator()]),
    });
  }

  createService(): FormGroup {
    return this.formBuilder.group({
      service: new FormControl(null, Validators.required),
    });
  }

  createFee(): FormGroup {
    return this.formBuilder.group({
      fee: new FormControl(null, Validators.required),
    });
  }

  createPaidCancellation(): FormGroup {
    return this.formBuilder.group({
      paidCancellation: new FormControl(null, Validators.required),
    });
  }

  private getFormValues<T>(formArray: FormArray): T[] {
    return formArray.controls.map((control) => control.value as T);
  }

  private isItemSelected(formArray: FormArray, controlKey: string, item: unknown): boolean {
    return formArray.controls.some((control) => control.value?.[controlKey] === item);
  }

  private productQuantityValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const group = control.parent as FormGroup;
      if (!group) return null;

      const product = group.get('product')?.value as ProductPurchased;
      const quantity = Number(control.value);

      if (!product || control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      if (!Number.isFinite(quantity) || quantity < 1 || !Number.isInteger(quantity)) {
        return { quantityInvalid: true };
      }

      if (quantity > product.quantity) {
        return { quantityExceeds: product.quantity };
      }

      return null;
    };
  }

  private clinicSupplyQuantityValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const group = control.parent as FormGroup;
      if (!group) return null;

      const clinicSupply = group.get('clinicSupply')?.value as ClinicSupplyPurchased;
      const quantity = Number(control.value);

      if (!clinicSupply || control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return { quantityInvalid: true };
      }

      const maxQuantity = this.getMinClinicSupplyRemaining(clinicSupply);
      if (quantity > maxQuantity) {
        return { quantityExceeds: maxQuantity };
      }

      return null;
    };
  }

  private creditQuantityValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const quantity = Number(control.value);

      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return { quantityInvalid: true };
      }

      const available = this.creditsPurchased ?? 0;
      if (quantity > available) {
        return { quantityExceeds: available };
      }

      return null;
    };
  }

  addProduct() {
    this.formReturnProducts.push(this.createProduct());
  }

  addClinicSupply() {
    this.formReturnClinicSupplies.push(this.createClinicSupply());
  }

  addCredit() {
    this.formReturnCredits.push(this.createCredit());
  }

  addService() {
    this.formReturnServices.push(this.createService());
  }

  removeProduct(i: number) {
    this.formReturnProducts.removeAt(i);
  }

  removeClinicSupply(i: number) {
    this.formReturnClinicSupplies.removeAt(i);
  }

  removeCredit() {
    if (this.formReturnCredits.length > 0) {
      this.formReturnCredits.removeAt(this.formReturnCredits.length - 1);
    }
  }

  removeService(i: number) {
    this.formReturnServices.removeAt(i);
  }

  detectInvoicePaid() {
    this.financeService.invoicePaid$.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.getServicesPurchased();
      this.getCreditsPurchased();
      this.getProductsPurchased();
      this.getClinicSupplies();
    });
  }

  getPatientId() {
    if (!this.patientId) {
      this.patientId = this.route.snapshot.params.patId.split('_')[0];
    }
  }

  private async getServicesPurchased() {
    this.servicesPurchased = await this.invoicesService.getServicesPurchasedByPatient(this.patientId).toPromise();
  }

  private async getCreditsPurchased() {
    const patient = await this.patientService.getPatientById(this.patientId).toPromise();
    this.creditsPurchased = patient.creditAmount ?? 0;
    this.formReturnCreditsControls.forEach((group) => group.get('creditQuantity')?.updateValueAndValidity());
  }

  private async getProductsPurchased() {
    this.productsPurchased = await this.invoicesService.getProductsPurchasedByPatient(this.patientId).toPromise();
  }

  private async getAdminFeesPurchased() {
    this.adminFeesPurchased = await this.invoicesService.getAdminFeesPurchasedByPatient(this.patientId).toPromise();
  }

  private async getPaidCancellations() {
    this.paidCancellations = await this.invoicesService.getPaidCancellationsByPatient(this.patientId).toPromise();
  }

  private async getClinicSupplies() {
    this.clinicSupplies = await this.invoicesService.getClinicSuppliesPurchasedByPatient(this.patientId).toPromise();
  }

  isExistItemInArray(item: any, array: any[]) {
    if (item.productId) {
      return array.some((invoiceLineItem) => item.productId === invoiceLineItem.productId);
    } else {
      return array.some((invoiceLineItem) => item.serviceTemplateId === invoiceLineItem.serviceTemplateId);
    }
  }

  isSelectedProduct(item: ProductPurchased) {
    return this.isItemSelected(this.formReturnProducts, 'product', item);
  }

  isSelectedClinicSupply(item: ClinicSupplyPurchased) {
    return this.isItemSelected(this.formReturnClinicSupplies, 'clinicSupply', item);
  }

  isSelectedService(item: ServicePurchased) {
    return this.isItemSelected(this.formReturnServices, 'service', item);
  }

  isSelectedFee(item: AdminFeePurchased) {
    return this.isItemSelected(this.formReturnFees, 'fee', item);
  }

  isSelectedPaidCancellation(item: PaidCancellation) {
    return this.isItemSelected(this.formPaidCancellations, 'paidCancellation', item);
  }

  async onReturnFormSubmit() {
    this.loading = true;
    const handler = this.returnHandlers[this.returnType];
    const returnInvoice = handler ? await handler() : undefined;
    this.loading = false;
    this.plannedTreatmentMultipleList = [];

    if (returnInvoice) {
      this.isOpenPaymentModal = true;
      this.selectedInvoice = returnInvoice;
    }
  }

  private async handleProductReturn(): Promise<Invoice | undefined> {
    const productsToReturn = this.getFormValues<{ product: ProductPurchased; productQuantity: number | string }>(
      this.formReturnProducts
    )
      .filter(({ product }) => !!product)
      .map(({ product, productQuantity }) => {
        if (productQuantity) {
          product.quantity = Number(productQuantity);
        }
        return product;
      });

    if (!productsToReturn.length) {
      return undefined;
    }

    return this.invoicesService.createReturnInvoiceForProductsPurchased(this.patientId, productsToReturn).toPromise();
  }

  private async handleClinicSupplyReturn(): Promise<Invoice | undefined> {
    const clinicSuppliesToReturn = this.getFormValues<{
      clinicSupply: ClinicSupplyPurchased;
      clinicSupplyQuantity: number | string;
    }>(this.formReturnClinicSupplies)
      .filter(({ clinicSupply, clinicSupplyQuantity }) => clinicSupply && Number(clinicSupplyQuantity) > 0)
      .map(({ clinicSupply, clinicSupplyQuantity }): ClinicSupplyReturn => {
        const quantity = Number(clinicSupplyQuantity);
        const remaining = this.getMinClinicSupplyRemaining(clinicSupply);
        const cappedQuantity = Math.max(1, Math.min(quantity, remaining));
        return {
          observationListId: clinicSupply.observationListId,
          quantity: cappedQuantity,
          unitPrice: clinicSupply.unitPrice,
          invoiceLineItemId: clinicSupply.invoiceLineItemId,
        };
      });

    if (!clinicSuppliesToReturn.length) {
      return undefined;
    }

    return this.invoicesService
      .createReturnInvoiceForClinicSupplies(this.patientId, clinicSuppliesToReturn)
      .toPromise();
  }

  private async handleServiceReturn(): Promise<Invoice | undefined> {
    const servicesToRefund: ServicePurchased[] = [];

    this.getFormValues<{ service: ServicePurchased }>(this.formReturnServices)
      .filter(({ service }) => !!service)
      .forEach(({ service }) => {
        if (service.plannedTreatmentMultipleId && this.plannedTreatmentMultiple) {
          this.plannedTreatmentMultipleList.forEach((plannedTreatment) => {
            servicesToRefund.push({
              name: service.name,
              purchaseDate: service.purchaseDate,
              unitPrice: service.unitPrice / this.plannedTreatmentMultiple.quantity,
              pricePaid: this.plannedTreatmentMultiple.price,
              serviceId: plannedTreatment.service.serviceId,
              quantity: 1,
              invoiceLineItemId: service.invoiceLineItemId,
              invoiceId: service.invoiceId,
              serviceTemplateId: service.serviceTemplateId,
              serviceTypeDescription: `Package Refund - 1/${this.plannedTreatmentMultiple.quantity}`,
              plannedTreatmentMultipleId: service.plannedTreatmentMultipleId,
              plannedTreatmentMultiple: this.plannedTreatmentMultiple,
            });
          });
        } else {
          servicesToRefund.push(service);
        }
      });

    if (!servicesToRefund.length) {
      return undefined;
    }

    const continueRefund = await this.returnService.checkForServiceCancellation(servicesToRefund);
    if (!continueRefund) {
      return undefined;
    }

    return this.invoicesService.createReturnInvoiceForServicesPurchased(this.patientId, servicesToRefund).toPromise();
  }

  private async handleCreditReturn(): Promise<Invoice | undefined> {
    const creditsToRefund = this.getFormValues<{ creditQuantity: number | string }>(this.formReturnCredits)
      .map(({ creditQuantity }) => Number(creditQuantity))
      .filter((credit) => !isNaN(credit));

    if (!creditsToRefund.length) {
      return undefined;
    }

    return this.invoicesService.createReturnInvoiceForCreditsPurchased(this.patientId, creditsToRefund).toPromise();
  }

  private async handleAdminFeeReturn(): Promise<Invoice | undefined> {
    const feesToRefund = this.getFormValues<{ fee: AdminFeePurchased }>(this.formReturnFees)
      .filter(({ fee }) => !!fee)
      .map(({ fee }) => {
        const adjustedFee = new AdminFeePurchased({ ...fee, pricePaid: fee.pricePaid * -1 });
        adjustedFee.serviceTypeDescription += ` for Invoice #${adjustedFee.invoiceId}`;
        return adjustedFee;
      });

    if (!feesToRefund.length) {
      return undefined;
    }

    return this.invoicesService.createReturnInvoiceForAdminFeesPurchased(this.patientId, feesToRefund).toPromise();
  }

  private async handlePaidCancellationReturn(): Promise<Invoice | undefined> {
    const paidCancellationsToRefund = this.getFormValues<{ paidCancellation: PaidCancellation }>(
      this.formPaidCancellations
    )
      .filter(({ paidCancellation }) => !!paidCancellation)
      .map(({ paidCancellation }) => {
        const refund = new PaidCancellation(paidCancellation);
        refund.description += ` for Invoice #${refund.invoiceId}`;
        return refund;
      });

    if (!paidCancellationsToRefund.length) {
      return undefined;
    }

    this.paidCancellation = paidCancellationsToRefund[0];

    return this.invoicesService
      .createReturnInvoiceForPaidCancellations(this.patientId, paidCancellationsToRefund)
      .toPromise();
  }

  nextDisabled(): boolean {
    const check = this.nextDisabledChecks[this.returnType];
    return check ? check() : true;
  }

  onSelectProductsPurchased(index: number) {
    const formGroup = this.formReturnProducts.at(index) as FormGroup;
    const quantityControl = formGroup?.get('productQuantity');
    quantityControl?.setValue(1);
    quantityControl?.markAsTouched();
    quantityControl?.updateValueAndValidity();
  }

  onSelectClinicSupply(index: number) {
    const formGroup = this.formReturnClinicSupplies.at(index) as FormGroup;
    const selectedSupply = formGroup?.get('clinicSupply')?.value as ClinicSupplyPurchased;
    const defaultQuantity = this.getMinClinicSupplyRemaining(selectedSupply);
    const quantityControl = formGroup?.get('clinicSupplyQuantity');
    quantityControl?.setValue(defaultQuantity ?? 1);
    quantityControl?.markAsTouched();
    quantityControl?.updateValueAndValidity();
  }

  onSelectServicePurchased(index: number) {
    this.plannedTreatmentMultiplesForm.reset();
    this.plannedTreatmentMultipleList = [];
    this.plannedTreatmentMultiple = null;

    const returnService = this.formReturnServices.at(index).value?.service as ServicePurchased;

    if (returnService?.plannedTreatmentMultipleId) {
      var plannedTreatmentMultipleId = returnService.plannedTreatmentMultipleId;
      this.treatmentPlanService
        .getPlannedTreatmentMultiple(plannedTreatmentMultipleId)
        .pipe(takeUntil(this.unsub))
        .subscribe((plannedTreatmentMultiple: PlannedTreatmentMultiple) => {
          this.plannedTreatmentMultiple = plannedTreatmentMultiple;
          this.isPlannedTreatmentMultiple = true;
        });
    } else {
      this.isPlannedTreatmentMultiple = false;
      this.plannedTreatmentMultiple = null;
    }
  }

  plannedTreatmentMultipleClick(plannedTreatment: PlannedTreatment) {
    if (this.plannedTreatmentMultipleList.includes(plannedTreatment)) {
      this.plannedTreatmentMultipleList.splice(this.plannedTreatmentMultipleList.indexOf(plannedTreatment), 1);
    } else {
      this.plannedTreatmentMultipleList.push(plannedTreatment);
    }
  }

  getMinClinicSupplyRemaining(clinicSupply: ClinicSupplyPurchased) {
    return Math.min(clinicSupply.invoiceLineItemRemainingQuantity, clinicSupply.observationListRemainingQuantity);
  }

  onInvoicePay() {
    this.initForm();
    this.isOpenPaymentModal = false;
    this.getPurchases();
    this.invoicePay.next(true);
  }

  async backToReturnPanel() {
    await this.removeInvoice(this.selectedInvoice);
    this.selectedInvoice = null;
    this.initForm();
    this.isOpenPaymentModal = false;
    this.getPurchases();
  }

  private checkIfInvoiceUnpaid() {
    const invoice = this.selectedInvoice;
    if (invoice && invoice.id && !invoice.isPaid) {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '300px',
        data: {
          title: 'Leave Invoice',
          content:
            'An Invoice has been created for this return but has not yet been paid. Do you want to save it for later?',
          confirmButtonText: 'Save',
          cancelButtonText: 'Remove',
          showCancel: true,
        },
      });

      dialogRef.afterClosed().subscribe(async (result) => {
        if (result === 'confirm') {
          this.invoicesService.invoicesListUpdated.next(invoice);
        } else {
          await this.removeInvoice(invoice);
        }
        this.selectedInvoice = null;
        this.isOpenPaymentModal = false;
        this.initForm();
        this.getPurchases();
      });
    }
  }

  private async removeInvoice(invoice: Invoice) {
    if (invoice && invoice.id && !invoice.isPaid) {
      this.loading = true;
      try {
        if (invoice.invoiceLineItems?.length) {
          var invoiceLineItems = invoice.invoiceLineItems;
          await Promise.all(
            invoiceLineItems.map((lineItem) => this.invoicesService.deleteInvoiceLineItem(lineItem.id).toPromise())
          );
          await this.invoicesService.deleteInvoice(invoice.id).toPromise();
        } else {
          await this.invoicesService.deleteInvoice(invoice.id).toPromise();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    }
  }

  ngOnDestroy() {
    this.checkIfInvoiceUnpaid();
    this.unsub.next();
    this.unsub.complete();
  }
}
